import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import * as api from '~/Services/Service';
import { TIPOS_CONTA_PREFEITURA_SEM_CONVENIO } from '~/constant';

// W-0539 — Modal de cadastro de vínculo (GE, Conta) — paridade Delphi uContaPrefeituraSemConvenio.pas:184-220.
// LICOES aplicadas:
//   §1.6 Yup completo (required + tipo + dependência)
//   ciclo 9 L3 race-guard em useEffect com fetch encadeado (let ativo = true / cleanup)
//   ciclo 9 L4 erro API — toast com extrairMensagemErro

const validationSchema = Yup.object().shape({
  codigoGrupoEmpresa: Yup.number()
    .typeError('Selecione um Grupo Empresa.')
    .required('Selecione um Grupo Empresa.')
    .positive('Grupo Empresa inválido.')
    .integer(),
  codigoConta: Yup.number()
    .typeError('Selecione uma Conta.')
    .required('Selecione uma Conta.')
    .positive('Conta inválida.')
    .integer()
    .when('codigoGrupoEmpresa', {
      is: (val) => val == null,
      then: Yup.number().notRequired().nullable(),
    }),
});

const INITIAL_VALUES = {
  codigoGrupoEmpresa: null,
  codigoConta: null,
};

const extrairMensagemErro = (err, fallback) => {
  if (err && err.message) return err.message;
  if (typeof err === 'string') return err;
  return fallback;
};

const ContasPorGeLoader = ({
  codigoGrupoEmpresa,
  contasPorGe,
  setCarregandoContas,
  setContasPorGe,
  setFieldValue,
}) => {
  useEffect(() => {
    if (!codigoGrupoEmpresa) {
      setCarregandoContas(false);
      return undefined;
    }

    if (contasPorGe[codigoGrupoEmpresa]) {
      setCarregandoContas(false);
      return undefined;
    }

    let ativo = true;
    setCarregandoContas(true);
    api
      .listarLookupContasPorTipos(
        codigoGrupoEmpresa,
        TIPOS_CONTA_PREFEITURA_SEM_CONVENIO
      )
      .then((res) => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        setContasPorGe((prev) => ({
          ...prev,
          [codigoGrupoEmpresa]: lista.map((c) => ({
            value: c.codigo_conta,
            label: `${c.codigo_conta} - ${c.apelido || ''}`,
            apelido: c.apelido || '',
          })),
        }));
      })
      .catch((err) => {
        if (!ativo) return;
        setFieldValue('codigoConta', null, false);
        toast.error(
          extrairMensagemErro(err, 'Erro ao carregar contas elegíveis.')
        );
      })
      .finally(() => {
        if (ativo) setCarregandoContas(false);
      });

    return () => {
      ativo = false;
    };
  }, [
    codigoGrupoEmpresa,
    contasPorGe,
    setCarregandoContas,
    setContasPorGe,
    setFieldValue,
  ]);

  return null;
};

const ModalCadastro = ({ show, onHide, onSuccess }) => {
  const formikRef = useRef(null);
  const [opcoesGe, setOpcoesGe] = useState([]);
  const [carregandoGe, setCarregandoGe] = useState(false);
  const [contasPorGe, setContasPorGe] = useState({});
  const [carregandoContas, setCarregandoContas] = useState(false);
  const [confirmCancelar, setConfirmCancelar] = useState(false);

  // Carrega lista de GEs uma única vez quando o modal abre — cache local em `opcoesGe`.
  useEffect(() => {
    if (!show) return undefined;
    if (opcoesGe.length > 0) return undefined;
    let ativo = true;
    setCarregandoGe(true);
    api
      .listarTodosGruposEmpresa()
      .then((res) => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        setOpcoesGe(
          lista.map((g) => ({
            value: g.codigo,
            label: `${g.codigo} - ${g.apelido || ''}`,
            apelido: g.apelido || '',
          }))
        );
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err, 'Erro ao carregar Grupos Empresa.'));
      })
      .finally(() => {
        if (ativo) setCarregandoGe(false);
      });
    return () => {
      ativo = false;
    };
  }, [show, opcoesGe.length]);

  const resetTudo = () => {
    setConfirmCancelar(false);
  };

  const handleClose = (values) => {
    const algumValor =
      values && (values.codigoGrupoEmpresa != null || values.codigoConta != null);
    if (algumValor) {
      setConfirmCancelar(true);
    } else {
      onHide();
    }
  };

  const handleModalHide = () => {
    const values = formikRef.current ? formikRef.current.values : {};
    handleClose(values);
  };

  const handleConfirmarCancelamento = (confirmado) => {
    setConfirmCancelar(false);
    if (confirmado) {
      resetTudo();
      onHide();
    }
  };

  const handleSubmit = (values, { setSubmitting }) => {
    api
      .cadastrarContaPrefeituraSemConvenio({
        codigo_grupo_empresa: values.codigoGrupoEmpresa,
        codigo_conta: values.codigoConta,
      })
      .then(() => {
        toast.success('Operação concluída.');
        onSuccess();
      })
      .catch((err) => {
        const msg = extrairMensagemErro(err, 'Erro ao cadastrar vínculo.');
        toast.error(msg);
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleModalHide}
        centered
        backdrop="static"
      >
        <Formik
          innerRef={formikRef}
          initialValues={INITIAL_VALUES}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleSubmit: formikSubmit,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
            resetForm,
          }) => {
            const opcoesContaAtuais =
              values.codigoGrupoEmpresa &&
              contasPorGe[values.codigoGrupoEmpresa]
                ? contasPorGe[values.codigoGrupoEmpresa]
                : [];
            const geValue =
              values.codigoGrupoEmpresa != null
                ? opcoesGe.find((o) => o.value === values.codigoGrupoEmpresa) ||
                  null
                : null;
            const contaValue =
              values.codigoConta != null
                ? opcoesContaAtuais.find((o) => o.value === values.codigoConta) ||
                  null
                : null;

            const onCloseClick = () => {
              handleClose(values);
            };

            return (
              <Form noValidate onSubmit={formikSubmit}>
                <ContasPorGeLoader
                  codigoGrupoEmpresa={values.codigoGrupoEmpresa}
                  contasPorGe={contasPorGe}
                  setCarregandoContas={setCarregandoContas}
                  setContasPorGe={setContasPorGe}
                  setFieldValue={setFieldValue}
                />
                <Modal.Header
                  style={{ backgroundColor: '#0747a6', color: '#fff' }}
                  closeButton
                  closeVariant="white"
                >
                  <Modal.Title style={{ color: '#fff' }}>
                    Novo vínculo de Conta Prefeitura Sem Convênio
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Row>
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>
                          Grupo Empresa <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                          inputId="cadastro-ge"
                          options={opcoesGe}
                          placeholder={
                            carregandoGe
                              ? 'Carregando...'
                              : 'Selecione o Grupo Empresa...'
                          }
                          isLoading={carregandoGe}
                          isClearable
                          value={geValue}
                          onChange={(opt) => {
                            const novoGe = opt ? opt.value : null;
                            setFieldValue('codigoGrupoEmpresa', novoGe);
                            setFieldValue('codigoConta', null);
                            setFieldTouched('codigoGrupoEmpresa', true, false);
                          }}
                          components={{ DropdownIndicator }}
                          styles={customStyles}
                          noOptionsMessage={() => 'Nenhum Grupo Empresa disponível'}
                        />
                        {touched.codigoGrupoEmpresa &&
                          errors.codigoGrupoEmpresa && (
                            <div
                              className="text-danger"
                              style={{ fontSize: '0.875em', marginTop: 4 }}
                            >
                              {errors.codigoGrupoEmpresa}
                            </div>
                          )}
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>
                          Conta <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                          inputId="cadastro-conta"
                          options={opcoesContaAtuais}
                          placeholder={
                            !values.codigoGrupoEmpresa
                              ? 'Selecione um Grupo Empresa antes'
                              : carregandoContas
                              ? 'Carregando contas...'
                              : 'Selecione a Conta...'
                          }
                          isDisabled={!values.codigoGrupoEmpresa}
                          isLoading={carregandoContas}
                          isClearable
                          value={contaValue}
                          onChange={(opt) => {
                            setFieldValue('codigoConta', opt ? opt.value : null);
                            setFieldTouched('codigoConta', true, false);
                          }}
                          components={{ DropdownIndicator }}
                          styles={customStyles}
                          noOptionsMessage={() =>
                            !values.codigoGrupoEmpresa
                              ? 'Selecione um Grupo Empresa antes'
                              : 'Nenhuma conta elegível para essa GE'
                          }
                        />
                        {touched.codigoConta && errors.codigoConta && (
                          <div
                            className="text-danger"
                            style={{ fontSize: '0.875em', marginTop: 4 }}
                          >
                            {errors.codigoConta}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={onCloseClick}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting}
                    onClick={() => {
                      // Garante exibição inline dos erros se o usuário tentar submeter vazio.
                      setFieldTouched('codigoGrupoEmpresa', true, false);
                      setFieldTouched('codigoConta', true, false);
                    }}
                  >
                    {isSubmitting ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      'Salvar'
                    )}
                  </Button>
                </Modal.Footer>

                <ModalConfirmacao
                  show={confirmCancelar}
                  title="Cancelar cadastro"
                  message="Deseja cancelar o cadastro? Os dados preenchidos serão descartados."
                  onClose={(ok) => {
                    if (ok) {
                      resetForm();
                    }
                    handleConfirmarCancelamento(ok);
                  }}
                />
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default ModalCadastro;
